<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';

import TeamForm from '../TeamForm.vue';
import AgentSelector from '../AgentSelector.vue';
import { useRouter } from 'vue-router';
import PageHeader from '../../SettingsSubPageHeader.vue';
import Spinner from 'shared/components/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const sameMembers = (a, b) =>
  a.length === b.length && [...a].sort().join(',') === [...b].sort().join(',');

// Editar time era um assistente de 3 passos: para trocar so a descricao,
// salvar levava aos participantes e depois a uma tela de conclusao, e nao dava
// para deixar o time sem ninguem. Agora nome, descricao e participantes ficam
// numa pagina so, com um botao Salvar, e zero participantes vale (com aviso).
export default {
  components: {
    TeamForm,
    AgentSelector,
    PageHeader,
    Spinner,
    Button,
  },
  setup() {
    return { router: useRouter() };
  },
  data() {
    return {
      isSaving: false,
      notFound: false,
      selectedAgents: [],
      initialMembers: [],
      membersLoaded: false,
      isLoadingMembers: false,
      membersLoadError: false,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'teams/getUIFlags',
      agentList: 'agents/getAgents',
    }),
    teamId() {
      return this.$route.params.teamId;
    },
    teamData() {
      return this.$store.getters['teams/getTeam'](this.teamId);
    },
    showTeamForm() {
      const { id } = this.teamData;
      return id && !this.uiFlags.isFetching;
    },
    membersChanged() {
      return !sameMembers(this.selectedAgents, this.initialMembers);
    },
    willBeEmpty() {
      return this.membersLoaded && this.selectedAgents.length === 0;
    },
  },
  mounted() {
    if (!this.teamData.id) this.fetchTeam();
    this.loadMembers();
  },
  methods: {
    // Time fora da lista (URL digitada ou link de time apagado): sem isto o
    // spinner ficava para sempre. Busca direto e, se nao existir, avisa.
    async fetchTeam() {
      try {
        await this.$store.dispatch('teams/show', {
          id: this.teamId,
        });
      } catch (error) {
        this.notFound = true;
      }
    },
    async loadMembers() {
      if (this.isLoadingMembers) return;
      this.isLoadingMembers = true;
      this.membersLoadError = false;
      try {
        await Promise.all([
          this.$store.dispatch('agents/get', { throwOnError: true }),
          this.$store.dispatch('teamMembers/get', { teamId: this.teamId }),
        ]);
        const members = this.$store.getters['teamMembers/getTeamMembers'](
          this.teamId
        );
        this.initialMembers = members.map(item => item.id);
        this.selectedAgents = [...this.initialMembers];
        this.membersLoaded = true;
      } catch {
        this.membersLoadError = true;
      } finally {
        this.isLoadingMembers = false;
      }
    },
    updateSelectedAgents(newAgentList) {
      this.selectedAgents = [...newAgentList];
    },
    async updateTeam(data) {
      if (this.isSaving) return;
      this.isSaving = true;
      const { teamId } = this;
      try {
        await this.$store.dispatch('teams/update', { id: teamId, ...data });
      } catch (error) {
        useAlert(this.$t('TEAMS_SETTINGS.TEAM_FORM.ERROR_MESSAGE'));
        this.isSaving = false;
        return;
      }

      // Participantes so vao quando carregaram e mudaram: com a leitura
      // falhando, salvar a lista vazia apagaria todo mundo do time.
      if (this.membersLoaded && this.membersChanged) {
        try {
          await this.$store.dispatch('teamMembers/update', {
            teamId,
            agentsList: this.selectedAgents,
          });
          this.initialMembers = [...this.selectedAgents];
          this.$store.dispatch('teams/get');
        } catch (error) {
          // error.message vinha do store como "AxiosError: Request failed..."
          useAlert(this.$t('TEAMS_SETTINGS.AGENTS.SAVE_ERROR'));
          this.isSaving = false;
          return;
        }
      }

      this.isSaving = false;
      useAlert(this.$t('TEAMS_SETTINGS.EDIT_FLOW.SAVE_SUCCESS'));
      this.router.replace({
        name: 'settings_teams_list',
        params: this.$route.params,
      });
    },
  },
};
</script>

<template>
  <div class="h-full w-full p-8 col-span-8 overflow-y-auto">
    <PageHeader
      :header-title="$t('TEAMS_SETTINGS.EDIT_FLOW.CREATE.TITLE')"
      :header-content="$t('TEAMS_SETTINGS.EDIT_FLOW.CREATE.DESC')"
    />
    <div class="flex flex-wrap">
      <TeamForm
        v-if="showTeamForm"
        :on-submit="updateTeam"
        :submit-in-progress="isSaving"
        :submit-button-text="$t('TEAMS_SETTINGS.EDIT_FLOW.CREATE.BUTTON_TEXT')"
        :form-data="teamData"
      >
        <section
          class="flex flex-col gap-3 pt-2"
          aria-labelledby="team-members-heading"
          data-testid="team-members-section"
        >
          <div>
            <h3
              id="team-members-heading"
              class="m-0 text-heading-3 text-n-slate-12"
            >
              {{ $t('TEAMS_SETTINGS.EDIT_FLOW.AGENTS.SECTION_TITLE') }}
            </h3>
            <p class="m-0 mt-1 text-body-main text-n-slate-11">
              {{ $t('TEAMS_SETTINGS.EDIT_FLOW.AGENTS.DESC') }}
            </p>
          </div>
          <div
            v-if="membersLoadError"
            role="alert"
            class="flex flex-col items-start gap-3 py-2"
          >
            <p class="m-0 text-sm text-n-ruby-11">
              {{ $t('TEAMS_SETTINGS.AGENTS.LOAD_ERROR') }}
            </p>
            <Button
              type="button"
              class="min-h-11"
              :label="$t('TEAMS_SETTINGS.AGENTS.RETRY')"
              @click="loadMembers"
            />
          </div>
          <template v-else-if="membersLoaded">
            <AgentSelector
              :agent-list="agentList"
              :selected-agents="selectedAgents"
              :update-selected-agents="updateSelectedAgents"
              :is-working="isSaving"
              :show-submit="false"
            />
            <p
              v-if="willBeEmpty"
              role="status"
              class="m-0 text-sm text-n-amber-11"
              data-testid="team-empty-warning"
            >
              {{ $t('TEAMS_SETTINGS.EDIT_FLOW.AGENTS.EMPTY_WARNING') }}
            </p>
          </template>
          <div v-else class="flex items-center justify-center py-6">
            <Spinner />
          </div>
        </section>
      </TeamForm>
      <div
        v-else-if="notFound"
        class="flex flex-col gap-2 text-sm text-n-slate-11"
        data-testid="team-not-found"
      >
        <p>{{ $t('TEAMS_SETTINGS.EDIT_FLOW.NOT_FOUND.TITLE') }}</p>
        <router-link
          :to="{ name: 'settings_teams_list', params: $route.params }"
          class="text-n-blue-11 hover:underline"
        >
          {{ $t('TEAMS_SETTINGS.EDIT_FLOW.NOT_FOUND.BACK') }}
        </router-link>
      </div>
      <Spinner v-else />
    </div>
  </div>
</template>
